import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    organizationName: '',
    comments: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email) {
      newErrors.email = 'Work email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      // Strip any non-digit characters for validation
      const digitsOnly = formData.phoneNumber.replace(/\D/g, '');
      // Most countries have different phone number lengths
      // This is a general check that can be adjusted based on specific requirements
      if (digitsOnly.length < 10) {
        newErrors.phoneNumber = 'Phone number must have at least 10 digits';
      }
    }
    if (!formData.organizationName) {
      newErrors.organizationName = 'Organization is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePhoneChange = (value, country) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: value
    }));
    if (errors.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Get the API URL from environment variable
        const scriptURL = "https://script.google.com/macros/s/AKfycbz_D9Ta8CKoZQoBrariausbsDVfoy080NIgDSFLd9SxVGnMRFRoCGXtWzq6JCS2fY0p/exec";
        
        // Create a FormData object
        const form = new FormData();
        form.append('timestamp', new Date().toISOString());
        form.append('videoId', 'contact-page');
        form.append('firstName', formData.firstName);
        form.append('lastName', formData.lastName);
        form.append('email', formData.email);
        form.append('phoneNumber', formData.phoneNumber);
        form.append('organizationName', formData.organizationName);
        form.append('comments', formData.comments || '');
        
        // Use URLSearchParams for compatibility
        const params = new URLSearchParams();
        for (const pair of form.entries()) {
          params.append(pair[0], pair[1]);
        }
        
        // Create a URL with query string
        const url = scriptURL + '?' + params.toString();
        
        // Use a simple image approach to avoid CORS
        const img = document.createElement('img');
        img.style.display = 'none';
        img.src = url;
        document.body.appendChild(img);
        
        // Remove the image after a delay
        setTimeout(() => {
          document.body.removeChild(img);
        }, 5000);
        
        setSubmitSuccess(true);
        console.log('Form submitted:', formData);
        
        // Reset form after successful submission
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          organizationName: '',
          comments: ''
        });
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (submitSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-12"
      >
        <h3 className="text-2xl font-bold text-green-600 mb-4">Thank You!</h3>
        <p className="text-gray-700 mb-6">
          Your message has been received. We'll get back to you shortly.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.form
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      onSubmit={handleSubmit}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="space-y-3">
          <Label htmlFor="firstName" className="text-base text-gray-700 font-medium">
            First Name*
          </Label>
          <Input
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            error={errors.firstName}
            className="h-12 text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Enter first name"
          />
          {errors.firstName && (
            <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
          )}
        </div>
        <div className="space-y-3">
          <Label htmlFor="lastName" className="text-base text-gray-700 font-medium">
            Last Name*
          </Label>
          <Input
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            error={errors.lastName}
            className="h-12 text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            placeholder="Enter last name"
          />
          {errors.lastName && (
            <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <Label htmlFor="email" className="text-base text-gray-700 font-medium">
          Work email*
        </Label>
        <Input
          id="email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleInputChange}
          error={errors.email}
          className="h-12 text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
          placeholder="name@company.com"
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </div>

      <div className="space-y-3">
        <Label htmlFor="phoneNumber" className="text-base text-gray-700 font-medium">
          Phone Number*
        </Label>
        <div className="phone-input-container w-full">
          <PhoneInput
            country={'us'} // Default country
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            inputProps={{
              id: 'phoneNumber',
              name: 'phoneNumber',
              required: true,
            }}
            containerClass="w-full"
            inputClass={`w-full h-12 text-base border-2 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:border-blue-500 focus:ring-blue-500 pl-12`}
            buttonClass="border-r-0"
            containerStyle={{
              height: '48px'
            }}
            inputStyle={{
              height: '48px',
              width: '100%'
            }}
            buttonStyle={{
              borderRight: 0
            }}
            dropdownClass="max-h-60 overflow-y-auto"
            preferredCountries={['us', 'gb', 'ca', 'au']}
            enableSearch={true}
          />
        </div>
        {errors.phoneNumber && (
          <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
        )}
      </div>

      <div className="space-y-3">
        <Label htmlFor="organizationName" className="text-base text-gray-700 font-medium">
          Organization*
        </Label>
        <Input
          id="organizationName"
          name="organizationName"
          value={formData.organizationName}
          onChange={handleInputChange}
          error={errors.organizationName}
          className="h-12 text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
          placeholder="Your company or organization"
        />
        {errors.organizationName && (
          <p className="text-red-500 text-sm mt-1">{errors.organizationName}</p>
        )}
      </div>

      <div className="space-y-3">
        <Label htmlFor="comments" className="text-base text-gray-700 font-medium">
          Message (Optional)
        </Label>
        <textarea
          id="comments"
          name="comments"
          value={formData.comments}
          onChange={handleInputChange}
          className="w-full h-32 text-base border-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-lg p-3"
          placeholder="Tell us how we can help you"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-14 bg-blue-600 text-white text-lg font-medium rounded-lg hover:bg-blue-700 transition-colors mt-6 disabled:bg-blue-400 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </motion.form>
  );
};

export default ContactForm; 